import type {
	InvitedAppShortcutAllowedSourceAppKey,
	InvitedAppShortcutAllowedTargetAppKey,
	InvitedAppShortcutLozengeState,
} from '../../common/types';

const GRAPHQL_ENDPOINT = '/gateway/api/graphql';
const OPERATION_NAME = 'invitedAppShortcutsCacheRefresh';
const USE_CASE = 'invitedAppShortcuts';

/**
 * Deliberately NOT a Relay query. The point of this request is the side effect —
 * GRS recomputes eligibility and repopulates its cache — so the response is
 * discarded, and normalising it into the Relay store risks a mid-view flip of the
 * lozenge the user is currently looking at. A plain POST keeps it out of the store
 * entirely. AGG accepts inline query documents (see `fetchAggResponse` in
 * `central-ai/ai-definitions`), so this needs no persisted-query registration.
 *
 * `__typename` is the whole selection: enough to be a valid query, nothing more.
 */
const QUERY = `query ${OPERATION_NAME}($context: GrowthRecContext) {
	growthRecommendations {
		recommendations(context: $context) {
			__typename
		}
	}
}`;

/**
 * Keys already refreshed, as `cloudId:sourceApp:targetApp`. Module-scoped so it
 * survives the frequent subtree remounts a `useRef` would not.
 *
 * This covers repeat renders and retries within one page. It does NOT cover other
 * tabs or a reload — those would need shared storage, and a duplicate refresh is
 * harmless anyway (GRS recomputes to the same value), so the extra request is a
 * better trade than a storage-consent dependency.
 *
 * `lozengeState` is deliberately absent from the key. Including it would *weaken*
 * the guard — two distinct states would permit two requests. Only `static` is ever
 * sent on the production path; a future client-side `animated` sender (the QA lever
 * in PUMA-2362) must revisit this.
 */
const refreshRequested = new Set<string>();

const describeError = (cause: unknown): { error: string; errorStack: string | undefined } =>
	cause instanceof Error
		? { error: cause.message, errorStack: cause.stack }
		: { error: String(cause), errorStack: undefined };

/**
 * Ask GRS to recompute the invited-app-shortcuts recommendation for this user so
 * the next SSR load serves the static variant instead of the animated one.
 *
 * Fire-and-forget by design: never awaited, never throws, and never touches the
 * Relay store. A failure just means the animated variant persists until the
 * eligible cache TTL expires — which is why the outcome is reported through
 * `onSucceeded` / `onFailed` rather than swallowed. Since PUMA-2246 was dropped
 * there is no shorter TTL backstop, so the failure rate is the signal that tells
 * us whether the animated-once guarantee actually holds.
 *
 * `lozengeState` is what GRS should cache next.
 */
export const requestInvitedAppShortcutsCacheRefresh = ({
	cloudId,
	sourceApp,
	targetApp,
	lozengeState,
	onSucceeded,
	onFailed,
}: {
	cloudId: string;
	lozengeState: InvitedAppShortcutLozengeState;
	onFailed?: (outcome: { error: string; errorStack: string | undefined }) => void;
	onSucceeded?: () => void;
	sourceApp: InvitedAppShortcutAllowedSourceAppKey;
	targetApp: InvitedAppShortcutAllowedTargetAppKey;
}): void => {
	if (!cloudId) {
		return;
	}

	const key = `${cloudId}:${sourceApp}:${targetApp}`;
	if (refreshRequested.has(key)) {
		return;
	}
	// Marked before the request, not after: a rejected refresh is not retried, so
	// recording the attempt is what keeps "exactly once" true.
	refreshRequested.add(key);

	fetch(`${GRAPHQL_ENDPOINT}?q=${OPERATION_NAME}`, {
		method: 'POST',
		credentials: 'same-origin',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			operationName: OPERATION_NAME,
			query: QUERY,
			variables: {
				context: {
					tenantId: cloudId,
					product: sourceApp,
					useCase: USE_CASE,
					custom: {
						refreshInvitedAppShortcutsCache: lozengeState,
					},
				},
			},
		}),
	})
		.then(async (response) => {
			if (!response.ok) {
				onFailed?.({
					error: `Request failed with status ${response.status}`,
					errorStack: undefined,
				});
				return;
			}

			// AGG answers GraphQL-level failures with HTTP 200 and an `errors` array.
			// The whole point of this request is the recompute, so a 200 carrying
			// errors is a failed refresh, not a success.
			const body: { errors?: Array<{ message?: string }> } = await response.json();
			const messages = body?.errors?.map((graphQlError) => graphQlError?.message).filter(Boolean);
			if (messages?.length) {
				onFailed?.({ error: messages.join('; '), errorStack: undefined });
				return;
			}

			onSucceeded?.();
		})
		.catch((cause: unknown) => {
			// Transport failure, or a body that would not parse.
			onFailed?.(describeError(cause));
		});
};

/**
 * Test-only reset for the module-scoped guard.
 */
export const clearInvitedAppShortcutsCacheRefreshGuard = (): void => {
	refreshRequested.clear();
};
